/**
 * DocumentsPage - Main documents management page
 * BP-04-02: Documents Frontend
 */

import { useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  FileText,
  FolderPlus,
  Upload,
  Search,
  Grid,
  List,
  Filter,
  Download,
  Trash2,
  Move,
  ChevronRight,
  Home,
  RefreshCw,
  Plus,
  X,
  Lock,
} from 'lucide-react';
import { useFamilyContext } from '@/features/family/hooks/useFamilyContext';
import { NoFamilyState } from '@/features/family/components/NoFamilyState';
import {
  useDocuments,
  useDocumentStats,
  useDeleteDocument,
  useBulkDeleteDocuments,
  useBulkMoveDocuments,
} from '../hooks/useDocuments';
import { useFolders, buildFolderTree, getFolderBreadcrumb } from '../hooks/useFolders';
import { useDocumentsStore } from '../stores/documentsStore';
import {
  DocumentGrid,
  DocumentList,
  DocumentFilters,
  FolderTree,
  FolderBreadcrumb,
  DocumentUploader,
  BulkActionBar,
  FolderCreateModal,
  DocumentMoveModal,
  DocumentViewer,
} from '../components';
import { ViewMode, DOCUMENT_TYPE_LABELS } from '../types/documentsTypes';
import type { Document, DocumentFolder } from '../types/documentsTypes';

export default function DocumentsPage() {
  const navigate = useNavigate();
  const { familyId, isLoading: isFamilyLoading, hasFamily } = useFamilyContext();

  // Store state
  const {
    viewMode,
    setViewMode,
    currentFolderId,
    setCurrentFolderId,
    selectedDocumentIds,
    clearSelection,
    expandedFolderIds,
    toggleFolderExpansion,
    filters,
    setFilters,
    clearFilters,
    isViewerOpen,
    viewerDocumentId,
    openViewer,
    closeViewer,
    isCreateFolderOpen,
    createFolderParentId,
    openCreateFolder,
    closeCreateFolder,
    isMoveModalOpen,
    moveDocumentIds,
    openMoveModal,
    closeMoveModal,
  } = useDocumentsStore();

  // Local state
  const [showFilters, setShowFilters] = useState(false);
  const [showUploader, setShowUploader] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Queries
  const {
    data: documentsData,
    isLoading: isLoadingDocs,
    error: docsError,
    refetch: refetchDocs,
  } = useDocuments({ familyId: familyId || '', enabled: !!familyId });

  const {
    data: foldersData,
    isLoading: isLoadingFolders,
  } = useFolders({ familyId: familyId || '', enabled: !!familyId });

  const { data: stats } = useDocumentStats(familyId || '', !!familyId);

  // Mutations
  const deleteDocument = useDeleteDocument(familyId || '');
  const bulkDelete = useBulkDeleteDocuments(familyId || '');
  const bulkMove = useBulkMoveDocuments(familyId || '');

  // Computed values
  const documents = documentsData?.documents ?? [];
  const folders = foldersData ?? [];
  const folderTree = buildFolderTree(folders);
  const breadcrumb = getFolderBreadcrumb(currentFolderId, folderTree);

  // Filter documents by search
  const filteredDocuments = searchQuery
    ? documents.filter(
        (doc) =>
          doc.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
          doc.original_filename.toLowerCase().includes(searchQuery.toLowerCase()) ||
          doc.tags.some((tag) => tag.toLowerCase().includes(searchQuery.toLowerCase()))
      )
    : documents;

  // Handlers
  const handleDocumentClick = useCallback((document: Document) => {
    openViewer(document.id);
  }, [openViewer]);

  const handleDocumentSelect = useCallback((documentId: string, selected: boolean) => {
    const { selectDocument, deselectDocument } = useDocumentsStore.getState();
    if (selected) {
      selectDocument(documentId);
    } else {
      deselectDocument(documentId);
    }
  }, []);

  const handleFolderClick = useCallback((folderId: string | null) => {
    setCurrentFolderId(folderId);
    clearSelection();
  }, [setCurrentFolderId, clearSelection]);

  const handleDelete = useCallback(async (document: Document) => {
    if (window.confirm(`Delete "${document.title}"? This action cannot be undone.`)) {
      await deleteDocument.mutateAsync(document.id);
    }
  }, [deleteDocument]);

  const handleBulkDelete = useCallback(async () => {
    if (selectedDocumentIds.length === 0) return;
    if (window.confirm(`Delete ${selectedDocumentIds.length} documents? This action cannot be undone.`)) {
      await bulkDelete.mutateAsync(selectedDocumentIds);
    }
  }, [selectedDocumentIds, bulkDelete]);

  const handleBulkMove = useCallback(async (targetFolderId: string | null) => {
    if (moveDocumentIds.length === 0) return;
    await bulkMove.mutateAsync({ documentIds: moveDocumentIds, targetFolderId });
    closeMoveModal();
  }, [moveDocumentIds, bulkMove, closeMoveModal]);

  // Loading state
  if (isFamilyLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sage" />
      </div>
    );
  }

  // No family state
  if (!hasFamily || !familyId) {
    return <NoFamilyState />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl font-semibold text-navy">Documents</h1>
          <p className="text-warm-gray mt-1">
            Store and organize important documents for your family
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setShowUploader(true)}
            className="inline-flex items-center gap-2 px-4 py-2 bg-sage text-white rounded-xl hover:bg-sage-dark transition-colors"
          >
            <Upload className="w-4 h-4" />
            Upload
          </button>
          <Link
            to="/family/documents/vault"
            className="inline-flex items-center gap-2 px-4 py-2 border-2 border-sage text-sage rounded-xl hover:bg-sage/5 transition-colors"
          >
            <Lock className="w-4 h-4" />
            Secure Vault
          </Link>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-xl p-4 border border-warm-gray/10">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-sage/10 rounded-lg flex items-center justify-center">
                <FileText className="w-5 h-5 text-sage" />
              </div>
              <div>
                <p className="text-2xl font-semibold text-navy">{stats.total_documents}</p>
                <p className="text-sm text-warm-gray">Total Documents</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-4 border border-warm-gray/10">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <Upload className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-semibold text-navy">{stats.recent_uploads}</p>
                <p className="text-sm text-warm-gray">This Month</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-4 border border-warm-gray/10">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-terracotta/10 rounded-lg flex items-center justify-center">
                <FileText className="w-5 h-5 text-terracotta" />
              </div>
              <div>
                <p className="text-2xl font-semibold text-navy">{stats.shared_with_attorney}</p>
                <p className="text-sm text-warm-gray">Shared</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-4 border border-warm-gray/10">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <Download className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-semibold text-navy">
                  {(stats.total_size_bytes / (1024 * 1024)).toFixed(1)} MB
                </p>
                <p className="text-sm text-warm-gray">Storage Used</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main Content Area */}
      <div className="flex gap-6">
        {/* Sidebar - Folder Tree */}
        <aside className="hidden lg:block w-64 flex-shrink-0">
          <div className="bg-white rounded-xl border border-warm-gray/10 overflow-hidden">
            <div className="p-4 border-b border-warm-gray/10 flex items-center justify-between">
              <h3 className="font-semibold text-navy">Folders</h3>
              <button
                onClick={() => openCreateFolder(null)}
                className="p-1.5 text-warm-gray hover:text-sage hover:bg-sage/10 rounded-lg transition-colors"
                title="Create Folder"
              >
                <FolderPlus className="w-4 h-4" />
              </button>
            </div>
            <div className="p-2">
              {/* Root folder */}
              <button
                onClick={() => handleFolderClick(null)}
                className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-left transition-colors ${
                  currentFolderId === null
                    ? 'bg-sage text-white'
                    : 'text-navy hover:bg-cream'
                }`}
              >
                <Home className="w-4 h-4" />
                <span className="font-medium">All Documents</span>
              </button>
              {/* Folder tree */}
              {folderTree.length > 0 && (
                <FolderTree
                  folders={folderTree}
                  selectedFolderId={currentFolderId}
                  expandedFolderIds={expandedFolderIds}
                  onSelectFolder={handleFolderClick}
                  onToggleExpand={toggleFolderExpansion}
                  onCreateFolder={openCreateFolder}
                />
              )}
            </div>
          </div>
        </aside>

        {/* Main Content */}
        <div className="flex-1 min-w-0">
          {/* Toolbar */}
          <div className="bg-white rounded-xl border border-warm-gray/10 p-4 mb-4">
            <div className="flex flex-col sm:flex-row sm:items-center gap-4">
              {/* Search */}
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-warm-gray" />
                <input
                  type="text"
                  placeholder="Search documents..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-warm-gray/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-sage/50 focus:border-sage"
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-warm-gray hover:text-navy"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>

              {/* Actions */}
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className={`p-2 rounded-lg transition-colors ${
                    showFilters ? 'bg-sage text-white' : 'text-warm-gray hover:bg-cream'
                  }`}
                  title="Filters"
                >
                  <Filter className="w-5 h-5" />
                </button>
                <button
                  onClick={() => refetchDocs()}
                  className="p-2 text-warm-gray hover:bg-cream rounded-lg transition-colors"
                  title="Refresh"
                >
                  <RefreshCw className="w-5 h-5" />
                </button>
                <div className="w-px h-6 bg-warm-gray/20" />
                <button
                  onClick={() => setViewMode('grid' as ViewMode)}
                  className={`p-2 rounded-lg transition-colors ${
                    viewMode === 'grid' ? 'bg-sage text-white' : 'text-warm-gray hover:bg-cream'
                  }`}
                  title="Grid View"
                >
                  <Grid className="w-5 h-5" />
                </button>
                <button
                  onClick={() => setViewMode('list' as ViewMode)}
                  className={`p-2 rounded-lg transition-colors ${
                    viewMode === 'list' ? 'bg-sage text-white' : 'text-warm-gray hover:bg-cream'
                  }`}
                  title="List View"
                >
                  <List className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Filters Panel */}
            {showFilters && (
              <div className="mt-4 pt-4 border-t border-warm-gray/10">
                <DocumentFilters
                  filters={filters}
                  onFilterChange={setFilters}
                  onClear={clearFilters}
                />
              </div>
            )}
          </div>

          {/* Breadcrumb */}
          {breadcrumb.length > 0 && (
            <div className="mb-4">
              <FolderBreadcrumb
                path={breadcrumb}
                onNavigate={handleFolderClick}
              />
            </div>
          )}

          {/* Bulk Actions */}
          {selectedDocumentIds.length > 0 && (
            <BulkActionBar
              selectedCount={selectedDocumentIds.length}
              onMove={() => openMoveModal(selectedDocumentIds)}
              onDelete={handleBulkDelete}
              onClear={clearSelection}
              isDeleting={bulkDelete.isPending}
            />
          )}

          {/* Document Grid/List */}
          <div className="bg-white rounded-xl border border-warm-gray/10 overflow-hidden">
            {isLoadingDocs ? (
              <div className="flex items-center justify-center py-16">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sage" />
              </div>
            ) : docsError ? (
              <div className="text-center py-16">
                <p className="text-red-500">Error loading documents</p>
                <button
                  onClick={() => refetchDocs()}
                  className="mt-2 text-sage hover:underline"
                >
                  Try again
                </button>
              </div>
            ) : filteredDocuments.length === 0 ? (
              <div className="text-center py-16">
                <FileText className="w-12 h-12 text-warm-gray/50 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-navy mb-2">
                  {searchQuery ? 'No documents found' : 'No documents yet'}
                </h3>
                <p className="text-warm-gray mb-4">
                  {searchQuery
                    ? 'Try adjusting your search or filters'
                    : 'Upload your first document to get started'}
                </p>
                {!searchQuery && (
                  <button
                    onClick={() => setShowUploader(true)}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-sage text-white rounded-xl hover:bg-sage-dark transition-colors"
                  >
                    <Upload className="w-4 h-4" />
                    Upload Document
                  </button>
                )}
              </div>
            ) : viewMode === 'grid' ? (
              <DocumentGrid
                documents={filteredDocuments}
                selectedIds={selectedDocumentIds}
                onSelect={handleDocumentSelect}
                onView={handleDocumentClick}
                onDelete={handleDelete}
                onMove={(doc) => openMoveModal([doc.id])}
              />
            ) : (
              <DocumentList
                documents={filteredDocuments}
                selectedIds={selectedDocumentIds}
                onSelect={handleDocumentSelect}
                onView={handleDocumentClick}
                onDelete={handleDelete}
                onMove={(doc) => openMoveModal([doc.id])}
              />
            )}
          </div>
        </div>
      </div>

      {/* Modals */}
      {showUploader && (
        <DocumentUploader
          familyId={familyId}
          folderId={currentFolderId}
          onClose={() => setShowUploader(false)}
          onSuccess={() => {
            setShowUploader(false);
            refetchDocs();
          }}
        />
      )}

      {isCreateFolderOpen && (
        <FolderCreateModal
          familyId={familyId}
          parentId={createFolderParentId}
          onClose={closeCreateFolder}
        />
      )}

      {isMoveModalOpen && (
        <DocumentMoveModal
          folders={folderTree}
          currentFolderId={currentFolderId}
          onMove={handleBulkMove}
          onClose={closeMoveModal}
          isMoving={bulkMove.isPending}
        />
      )}

      {isViewerOpen && viewerDocumentId && (
        <DocumentViewer
          documentId={viewerDocumentId}
          onClose={closeViewer}
        />
      )}
    </div>
  );
}
